import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  PlusCircle, FileText, Package, ArrowRight,
  FileClock, CheckCircle2, Wallet, Loader2,
} from "lucide-react";

export const STATUS_STYLE = {
  "Aguardando conferência": "bg-orange-500/10 text-orange-400 border-orange-800",
  "Orçamento conferido": "bg-lime-500/10 text-lime-400 border-lime-800",
  "Em análise": "bg-amber-500/10 text-amber-400 border-amber-800",
  "Em orçamento": "bg-yellow-500/10 text-yellow-400 border-yellow-800",
  "Orçamento enviado": "bg-sky-500/10 text-sky-400 border-sky-800",
  "Aguardando aprovação": "bg-violet-500/10 text-violet-400 border-violet-800",
  Aprovado: "bg-emerald-500/10 text-emerald-400 border-emerald-800",
  "Em produção": "bg-blue-500/10 text-blue-400 border-blue-800",
  "Concluído": "bg-teal-500/10 text-teal-400 border-teal-800",
  Entregue: "bg-orange-500/10 text-orange-400 border-orange-800",
  Rascunho: "bg-zinc-500/10 text-zinc-300 border-zinc-700",
  Reprovado: "bg-red-500/10 text-red-400 border-red-800",
  Cancelado: "bg-zinc-700/40 text-zinc-400 border-zinc-600",
};

export const brl = (v) =>
  (v ?? 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

export const fdate = (iso) =>
  iso ? new Date(iso).toLocaleDateString("pt-BR") : "—";

export function StatusBadge({ status }) {
  return (
    <span className={`inline-block text-[11px] font-medium uppercase tracking-wider border rounded-sm px-2 py-0.5 ${STATUS_STYLE[status] || STATUS_STYLE["Em análise"]}`}>
      {status || "Em análise"}
    </span>
  );
}

export default function PortalDashboard() {
  const { user } = useAuth();
  const [quotes, setQuotes] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/quote-requests");
        setQuotes(data);
      } catch {
        setQuotes([]);
      }
    })();
  }, []);

  const loading = quotes === null;
  const list = quotes || [];
  const cards = [
    { icon: FileText, label: "Orçamentos", value: list.length, testId: "portal-stat-quotes" },
    { icon: FileClock, label: "Em análise", value: list.filter((q) => q.status === "Em análise").length, testId: "portal-stat-pending" },
    { icon: CheckCircle2, label: "Aprovados", value: list.filter((q) => q.status === "Aprovado").length, testId: "portal-stat-approved" },
    { icon: Wallet, label: "Valor total", value: brl(list.reduce((s, q) => s + (q.value || 0), 0)), testId: "portal-stat-value" },
  ];
  const recent = list.slice(0, 5);

  return (
    <div data-testid="portal-dashboard-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="display text-3xl sm:text-4xl font-extrabold uppercase">Dashboard</h1>
          <p className="mt-1 text-sm text-zinc-500">
            Bem-vindo, <span className="text-zinc-300">{user?.name || user?.email}</span>
          </p>
        </div>
        <Link
          to="/painel/orcamentos/novo"
          data-testid="portal-new-quote-button"
          className="inline-flex items-center gap-2 bg-orange-600 hover:bg-orange-500 text-white text-sm font-semibold uppercase tracking-wider px-5 py-2.5 rounded-sm transition-colors"
        >
          <PlusCircle className="w-4 h-4" /> Novo Orçamento
        </Link>
      </div>

      <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-4">
        {cards.map((c) => (
          <div key={c.label} className="card-tactical rounded-md p-5" data-testid={c.testId}>
            <c.icon className="w-6 h-6 text-orange-500" strokeWidth={1.5} />
            <div className="display mt-3 text-2xl sm:text-3xl font-extrabold">
              {loading ? <Loader2 className="w-5 h-5 animate-spin text-zinc-600" /> : c.value}
            </div>
            <div className="text-xs uppercase tracking-wider text-zinc-500 mt-1">{c.label}</div>
          </div>
        ))}
      </div>

      <div className="mt-8 grid md:grid-cols-3 gap-4">
        {[
          { to: "/painel/orcamentos/novo", icon: PlusCircle, title: "Solicitar orçamento", desc: "Descreva sua peça e receba uma proposta em até 48h úteis.", testId: "portal-action-new" },
          { to: "/painel/orcamentos", icon: FileText, title: "Meus orçamentos", desc: "Acompanhe o status das suas solicitações.", testId: "portal-action-quotes" },
          { to: "/painel/pedidos", icon: Package, title: "Pedidos", desc: "Veja pedidos aprovados em produção e entregues.", testId: "portal-action-orders" },
        ].map((a) => (
          <Link
            key={a.to}
            to={a.to}
            data-testid={a.testId}
            className="group card-tactical rounded-md p-6 hover:border-orange-900/60 transition-colors"
          >
            <div className="flex items-center justify-between">
              <a.icon className="w-7 h-7 text-orange-500" strokeWidth={1.5} />
              <ArrowRight className="w-4 h-4 text-zinc-600 group-hover:text-orange-500 group-hover:translate-x-1 transition-all" />
            </div>
            <h3 className="display mt-4 text-xl font-bold uppercase tracking-wide">{a.title}</h3>
            <p className="mt-1.5 text-sm text-zinc-500 leading-relaxed">{a.desc}</p>
          </Link>
        ))}
      </div>

      <div className="mt-8 card-tactical rounded-md p-6" data-testid="portal-recent-quotes">
        <div className="flex items-center justify-between mb-4">
          <h2 className="display text-xl font-bold uppercase tracking-wide">Orçamentos recentes</h2>
          <Link to="/painel/orcamentos" className="text-xs font-semibold uppercase tracking-wider text-orange-500 hover:text-orange-400">
            Ver todos
          </Link>
        </div>
        {loading ? (
          <div className="py-8 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-zinc-600" /></div>
        ) : !recent.length ? (
          <div className="py-8 text-center text-sm text-zinc-500">
            Nenhum orçamento ainda.{" "}
            <Link to="/painel/orcamentos/novo" className="text-orange-500 hover:text-orange-400 font-medium">
              Solicite o primeiro
            </Link>
          </div>
        ) : (
          <ul className="divide-y divide-zinc-800">
            {recent.map((q) => (
              <li key={q.id} className="py-3 flex items-center justify-between gap-4" data-testid={`portal-recent-quote-${q.number}`}>
                <div>
                  <div className="mono text-sm text-zinc-200">{q.number}</div>
                  <div className="text-xs text-zinc-500 mt-0.5">
                    {fdate(q.created_at)} · {(q.services?.length ? q.services.join(" + ") : q.service_type) || "—"}
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <span className="mono text-sm text-zinc-300">{q.value != null ? brl(q.value) : "—"}</span>
                  <StatusBadge status={q.status} />
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
